import {Decoder} from '@raison/prelude';
import infoIcon from '@raison/static/icons/information-line.svg';
import {useSuspenseQuery} from '@tanstack/react-query';
import {type} from 'arktype';
import BigNumber from 'bignumber.js';
import React, {useEffect, useMemo, useState} from 'react';
import {useConversionRate, useCurrencyFormat} from '~/entities/currencies';
import {FallbackPage, NotFoundPage} from '~/entities/fallbacks';
import {TransactionHolder} from '~/entities/operations-crypto';
import {OperationDetail, OperationSelectButton} from '~/entities/operations-fiat';
import {executeOperationFinishNavigation} from '~/entities/transactions-history';
import {useUserInfoQueryOptions} from '~/entities/user';
import {
    InvalidCodeDialog,
    PinErrorCode,
    SmsDeliveryStatus,
    SmsErrorStatus,
    SusActivityDialog,
    TwoFaCodeForm,
    TwoFaMethodChoiceView,
    TwoFaProvider,
    useResendTimer,
    useSmsStatusPolling,
} from '~/features/two-factor';
import {
    connectFieldWith,
    connectSubmitToImmerForm,
    fieldBlurRecipe,
    fieldsCompleteRecipe,
    FormState,
    Issues,
    useImmerState,
    userInputRecipe,
    validateFormState,
} from '~/library/form-models.v1';
import {useHttpClient} from '~/library/network-facade';
import {AccountId} from '~/library/types';
import {
    RootLayout,
    RsnAmount,
    RsnAmountInput,
    RsnButton,
    RsnDialog,
    RsnRadioGroup,
    RsnSkeleton,
} from '~/library/ui-kit';
import {FeeRequestData, useFetchFeesOnSell} from '~/processes/crypto-sell-and-buy/api/fetchFeesOnSell';
import {define} from '~/processes/crypto-send-and-receive/lib/define';
import {useInHistoryStateNamespace} from '~/processes/crypto-send-and-receive/lib/useInHistoryState';
import {
    getDistance,
    NavigateToEntry,
    NavigationEntryPoint,
    useNavigateFromEntry,
    useNavigationEntryPoint,
} from '~/processes/crypto-send-and-receive/lib/useNavigateWithCount';
import {OrderId} from '~/processes/crypto-send-and-receive/model/TrTx';
import {useErrorHandler} from '~/processes/crypto-send-and-receive/model/useErrorHandler';
import {CalculatedAmounts, FeeCalculationType} from '~/processes/crypto-send-and-receive/ui/SendTxIntro';
import {OrderPlacedInfoPage} from '~/processes/crypto-send-and-receive/ui/TxPlacedPage';
import {assetsRequestRoutine} from '~/redux/assets/assetsActions';
import {useAppDispatch, useAppSelector} from '~/shared/store';
import {submitConfirmSellTxRequest} from '../api/submitConfirmSellTxRequest';
import {useSubmitSellTxRequest} from '../api/submitSellTxRequest';
import {BlockchainAccount, FiatAccount} from '../model/Accounts';
import {selectSellTxResource, SellTxLimits, SellTxResource} from '../model/SellTxResource';
import {ConfirmDetailsDialog, FeeHandlingDialog, NotEnoughFundsDialog} from './SellTx.view';

const AccountIdFromString = Decoder.prepare(Number)(AccountId);

const MINIMUM_ERROR_REGEX = /^The minimum transaction amount is ([0-9.]+)$/;

export function SellTxContainer({from: stringAccountIdFrom, to: stringAccountIdTo}: {from: string; to: string}) {
    const from = useMemo(() => Decoder.run(AccountIdFromString, stringAccountIdFrom).orThrow(), [stringAccountIdFrom]);
    const to = useMemo(() => Decoder.run(AccountIdFromString, stringAccountIdTo).orThrow(), [stringAccountIdFrom]);

    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(assetsRequestRoutine.trigger());
    }, []);

    const resource = useAppSelector(selectSellTxResource(from, to));

    if (resource.isError) {
        return <FallbackPage />;
    }

    if (resource.isUninitialized || resource.isLoading) {
        return <SellTxLoadingPage />;
    }

    if (!resource.data) {
        return <NotFoundPage />;
    }

    return <SellTxDirector resource={resource.data} />;
}

export function SellTxDirector({resource}: {resource: SellTxResource}) {
    const {accountFrom, accountTo} = resource;

    const http = useHttpClient();
    const dispatch = useAppDispatch();
    const entry = useNavigationEntryPoint();
    const navigate = useNavigateFromEntry(entry);
    const useInHistoryState = useInHistoryStateNamespace('send_dir', navigate);
    const handleError = useErrorHandler();
    const [direction, setDirection] = useInHistoryState(SellDirection, {
        view: 'form',
    });
    const {startPolling} = useSmsStatusPolling();

    const [holder, setHolder] = useState<TransactionHolder | null>();

    const queryOfUser = useSuspenseQuery(useUserInfoQueryOptions());

    const [mainForm, updateMainForm] = useImmerState(SellFormState);

    const mutateMain = useSubmitSellTxRequest(accountFrom.id, accountFrom.type);

    const {
        isPending: isFeePending,
        isDebouncing: isFeeDebouncing,
        data: {value: feeData, problem: feeIssues} = {},
    } = useFetchFeesOnSell(useFeeRequestData(mainForm.values, resource));

    const amountToReceive = feeData?.amountToBeReceived ? BigNumber(feeData.amountToBeReceived) : BigNumber(0);

    const [confirmForm, setConfirmForm] = useImmerState(SmsConfirmationFormState);

    const [timer$, setTimer] = useResendTimer();

    const currentFee = feeData?.feeAmount ? BigNumber(feeData.feeAmount) : BigNumber(0);

    function onPollingError(error: SmsErrorStatus) {
        setTimer.success(0);
        setDirection(current => {
            return current.view === 'two-fa-confirm' ? {...current, delivery: error} : current;
        });
    }

    function onRepeatedPollingSuccess() {
        setDirection(current => {
            return current.view === 'two-fa-confirm' ? {...current, delivery: 'DELIVERED'} : current;
        });
    }

    // The amount screen is the entry point of the flow, but the depth of the history
    // above it varies: 2fa dialogs and sms delivery dialogs add entries of their own.
    // So step back by the distance accumulated since the entry instead of a fixed count.
    function returnToAmountScreen() {
        const distance = getDistance(entry);
        if (distance > 0) {
            navigate(-distance);
        } else {
            setDirection({view: 'form'}, {replace: true});
        }
    }

    const requestCode = connectSubmitToImmerForm(setConfirmForm)(async (params: RequestCodeParams) => {
        const resending = params.resending ?? false;
        const provider = params?.provider;

        setConfirmForm(form => {
            form.values.codeFromSms = '';
            form.serverIssues.codeFromSms = undefined;
        });

        setTimer.pending();
        const {value: holder, problem: serverIssues} = await mutateMain({
            account: accountTo.id,
            amount: amountToReceive,
            fee: currentFee.toNumber(),
            repeated_event: resending ? 'true' : 'false',
            provider,
        }).catch(err => {
            setTimer.failure(err);
            return handleError(err);
        });

        if (serverIssues) {
            if (direction.view === 'two-fa-pick' || direction.view === 'two-fa-confirm') {
                returnToAmountScreen();
            }

            const newIssues: Issues<Values> = {};

            if (serverIssues.fee) {
                newIssues.fee = 'FEE_ERROR';
            }
            if (serverIssues.amount) {
                const match = serverIssues.amount.match(MINIMUM_ERROR_REGEX);
                if (match) {
                    newIssues.amount = t.minAmount(`${match[1]} ${resource.accountFrom.code}`);
                }
                if (serverIssues.amount.startsWith('Not enough')) {
                    newIssues.amount = t.notEnoughFunds;
                }
            }

            updateMainForm(state => {
                state.serverIssues = newIssues;
            });
            return;
        }

        setHolder(holder);
        setTimer.success(holder.expires_in);
        setDirection({view: 'two-fa-confirm', provider}, {replace: resending});
        if (provider.kind === 'sms') {
            startPolling({
                tenantId: holder.tenant_uuid,
                onError: onPollingError,
                ...(resending ? {onSuccess: onRepeatedPollingSuccess} : null),
            });
        }
    });

    const handleSubmit = connectSubmitToImmerForm(updateMainForm)(async (canSubmit: boolean) => {
        if (!canSubmit) {
            updateMainForm(fieldsCompleteRecipe('amount'));
            return;
        }

        actions.openDialog('confirm-details');
        return;
    });

    if (direction.view === 'finish') {
        const goToDetails = () => {
            executeOperationFinishNavigation(direction.orderId, navigate);
        };

        return (
            <OrderPlacedInfoPage
                goToDetails={goToDetails}
                amount={direction.qty}
                currency={resource.accountFrom}
                direction={'out'}
                details={'Sale request submitted.\nProcessing may take some time.'}
            />
        );
    }

    if (direction.view === 'two-fa-pick') {
        return (
            <RootLayout common title={`Sell ${resource.accountFrom.code}`}>
                <TwoFaMethodChoiceView
                    onMessagePick={method => requestCode({provider: {kind: 'sms', method}})}
                    onAppPick={() => requestCode({provider: {kind: 'otp'}})}
                    onActivationPick={() => setDirection({view: 'two-fa-pick', dialog: 'settings-prompt'})}
                    onMethodPickerOpen={() => setDirection({view: 'two-fa-pick', dialog: 'method-picker'})}
                    isAppActivated={queryOfUser.data.otpEnabled}
                    isActivationPromptOpen={direction.dialog === 'settings-prompt'}
                    onDialogClose={() => navigate(-1)}
                    isMethodPickerOpen={direction.dialog === 'method-picker'}
                />
            </RootLayout>
        );
    }

    if (direction.view === 'two-fa-confirm' && holder) {
        const provider = direction.provider;

        const submitTransactionSignature = connectSubmitToImmerForm(setConfirmForm)(async () => {
            const response = await submitConfirmSellTxRequest(http, {
                wallet: accountFrom,
                holder,
                codeFromSms: confirmForm.values.codeFromSms,
            }).catch(handleError);

            switch (response.status) {
                case 'Ok':
                    setDirection({
                        view: 'finish',
                        qty: RsnAmount.toString(mainForm.values.amount),
                        orderId: response.datum.id,
                    });
                    break;
                case 'BadRequest': {
                    const issue = response.datum;

                    if (issue === PinErrorCode.ExpiredCode) {
                        setConfirmForm(form => {
                            form.serverIssues = {
                                codeFromSms: 'This code has expired',
                            };
                        });
                    }

                    if (issue === PinErrorCode.InvalidCode) {
                        const onClose = () => {
                            setConfirmForm(form => void (form.values.codeFromSms = ''));
                            TwoFaCodeForm.focusOTPInput();
                        };
                        dispatch(InvalidCodeDialog.open({onClose}));
                    }

                    if (issue === PinErrorCode.SusActivity) {
                        dispatch(SusActivityDialog.open());
                    }

                    break;
                }
            }
        });

        const isSubmittingOtpForm = confirmForm.submitInitAt > confirmForm.submitDoneAt;

        return (
            <RootLayout common title={`Sell ${resource.accountFrom.code}`}>
                <TwoFaCodeForm.ForCryptoTx
                    timer$={timer$}
                    provider={provider}
                    onInputComplete={submitTransactionSignature}
                    onRetryIntent={() => requestCode({resending: true, provider})}
                    isPending={isSubmittingOtpForm}
                    otp={confirmForm.values.codeFromSms}
                    onOtpChange={codeFromSms => setConfirmForm(userInputRecipe({codeFromSms}))}
                    issue={confirmForm.serverIssues.codeFromSms}
                    userPhone={queryOfUser.data.username}
                    deliveryInfo={direction.delivery ?? null}
                    onDeliveryInfoDismiss={() => navigate(-1)}
                    onMethodChangeIntent={() => navigate(-1)}
                />
            </RootLayout>
        );
    }

    const actions = {
        handleSubmit,
        userInput(update: Partial<Values>) {
            updateMainForm(userInputRecipe(update));
        },
        fieldBlur(field: keyof Values) {
            updateMainForm(fieldBlurRecipe(field));
        },
        openDialog(name: DialogName) {
            setDirection({view: 'form', dialog: name});
        },
        feeInfoClick() {
            setDirection({view: 'form', dialog: 'fee-info'});
        },
        confirmDetailsClick() {
            setDirection({view: 'two-fa-pick'}, {replace: true});
        },
    };

    if (direction.view === 'form') {
        return (
            <RootLayout title={`Sell ${resource.accountFrom.code}`}>
                <SellTxForm
                    {...resource}
                    fee={feeData}
                    form={mainForm}
                    feeIssues={feeIssues ?? {}}
                    actions={actions}
                    isFeesPending={isFeePending || isFeeDebouncing}
                    dialog={'dialog' in direction ? direction.dialog : undefined}
                    entry={entry}
                />
            </RootLayout>
        );
    }

    return <NavigateToEntry entry={entry} />;
}

export function SellTxForm({
    form,
    accountFrom,
    accountTo,
    isFeesPending,
    actions,
    fee,
    dialog,
    entry,
    pathToBuy,
    limits,
    feeIssues,
}: {
    form: SellFormState;
    feeIssues: Issues<Values>;
    accountFrom: BlockchainAccount;
    accountTo: FiatAccount;
    fee?: CalculatedAmounts;
    isFeesPending?: boolean;
    dialog?: DialogName;
    entry: NavigationEntryPoint;
    limits: SellTxLimits;
    actions: {
        handleSubmit: (canSubmit: boolean) => void;
        userInput: (update: Partial<Values>) => void;
        fieldBlur: (field: keyof Values) => void;
        openDialog: (name: DialogName) => void;
        feeInfoClick: () => void;
        confirmDetailsClick: () => void;
    };
    pathToBuy: string | null;
}) {
    const navigate = useNavigateFromEntry(entry);
    const navigateBack = () => navigate(-1);

    const rate = useConversionRate({from: accountFrom, to: accountTo});

    const {format: formatAsSource} = useCurrencyFormat(accountFrom);
    const {format: formatAsTarget} = useCurrencyFormat(accountTo);
    const {format: formatAsApprox} = useCurrencyFormat(accountTo, {approx: true});
    const {format: formatAsFee} = useCurrencyFormat(accountFrom.blockchain);

    const renderAsSource = renderAsyncOperationDetail(!!isFeesPending, formatAsSource, {width: 70, height: 16});
    const renderAsApprox = renderAsyncOperationDetail(!!isFeesPending, formatAsApprox, {width: 100, height: 24});
    const renderAsFee = renderAsyncOperationDetail(!!isFeesPending, formatAsFee, {width: 80, height: 16});

    const amountValue = RsnAmount.toBigNumber(form.values.amount).decimalPlaces(
        accountFrom.decimals,
        BigNumber.ROUND_DOWN
    );

    const amountSold = fee?.amountToBeReceived ? BigNumber(fee.amountToBeReceived) : BigNumber(0);
    const amountSpend = fee?.amountToBeSent ? BigNumber(fee.amountToBeSent) : BigNumber(0);
    const currentFee = fee?.feeAmount ? BigNumber(fee.feeAmount) : BigNumber(0);
    const approxAmount =
        fee?.amountToBeReceived && !RsnAmount.equals(form.values.amount, RsnAmount.empty())
            ? BigNumber(fee?.amountToBeReceived).multipliedBy(rate)
            : BigNumber(0);

    const isOnOwnBlockchain = accountFrom.code === accountFrom.blockchain.currencyCode;
    const isAllFundsUsed = limits.upper.eq(amountValue);

    const minimumHintText = t.minAmount(formatAsSource(limits.lower));

    const {hasIssues, isSubmitting, ...validatedForm} = validateFormState(form, values => {
        const issues: Issues<Values> = {};

        if (!isOnOwnBlockchain && RsnAmount.toBigNumber(values.amount).gt(0)) {
            if (currentFee.isGreaterThan(limits.fee) || amountSpend.isGreaterThan(limits.upper)) {
                issues.fee = t.notEnoughFunds;
            }
        }

        if (RsnAmount.toBigNumber(values.amount).isLessThan(limits.lower)) {
            issues.amount = minimumHintText;
        }

        if (amountSpend.gt(limits.upper)) {
            if (RsnAmount.toBigNumber(values.amount).lte(limits.upper)) {
                issues.fee = 'FEE_ERROR';
            } else {
                issues.amount = t.notEnoughFunds;
            }
        }

        if (feeIssues?.amount?.startsWith('Not enough')) {
            issues.amount = t.notEnoughFunds;
        }

        return {values, issues};
    });

    const hasFeeIssue = !!validatedForm.issues.fee || !!validatedForm.serverIssues.fee;

    const canSubmit = !hasIssues && !isSubmitting && !isFeesPending;

    const connectField = connectFieldWith(validatedForm, actions.fieldBlur);

    return (
        <>
            <form
                className={'flex h-full flex-col'}
                onSubmit={e => {
                    e.preventDefault();
                    actions.handleSubmit(canSubmit);
                }}
            >
                <div className={'flex flex-1 flex-col gap-6 p-4'}>
                    <OperationSelectButton
                        label={'From'}
                        iconSrc={accountFrom.icon}
                        heading={accountFrom.name}
                        details={`Balance: ${formatAsSource(accountFrom.balance)}`}
                    />
                    <OperationSelectButton
                        label={'To'}
                        iconSrc={accountTo.icon}
                        heading={accountTo.name}
                        details={`Balance: ${formatAsTarget(accountTo.balance)}`}
                    />
                    {isOnOwnBlockchain ? (
                        <RsnRadioGroup
                            label={
                                <RsnRadioGroup.LabelWithInfo onInfoClick={actions.feeInfoClick}>
                                    Fee handling
                                </RsnRadioGroup.LabelWithInfo>
                            }
                            options={[
                                {label: 'Included', value: FeeCalculationType.Included},
                                {label: 'On top', value: FeeCalculationType.OnTop},
                            ]}
                            disabled={isAllFundsUsed}
                            value={form.values.fee}
                            onChange={fee => actions.userInput({fee})}
                        />
                    ) : (
                        <OperationSelectButton
                            label={'Pay fee from'}
                            iconSrc={accountFrom.blockchain.icon}
                            heading={accountFrom.blockchain.name}
                            details={`Balance: ${formatAsFee(accountFrom.blockchain.balance)}`}
                        />
                    )}
                </div>
                <div className={'flex flex-col gap-4 p-4'}>
                    <RsnAmountInput
                        suffix={accountFrom.code}
                        maxIntegers={10}
                        value={form.values.amount}
                        onChange={amount => actions.userInput({amount})}
                        maxDecimals={accountFrom.decimals}
                        hintAction={
                            <RsnAmountInput.HintActionUseAll
                                all={limits.upper.toString()}
                                used={isAllFundsUsed}
                                onClick={all =>
                                    actions.userInput({
                                        fee: FeeCalculationType.Included,
                                        amount: RsnAmount.fromString(all),
                                    })
                                }
                                text={'Use all funds'}
                            />
                        }
                        {...connectField('amount', {defaultHelperText: minimumHintText})}
                    />
                    <OperationDetail.Layout>
                        <OperationDetail
                            label={'Transaction fee'}
                            {...(hasFeeIssue && {
                                icon: {
                                    src: infoIcon,
                                    onClick: () => actions.openDialog('fee-issue'),
                                },
                                highlight: true,
                            })}
                        >
                            {renderAsFee(currentFee)}
                        </OperationDetail>
                        <OperationDetail label={'Amount sold'}>{renderAsSource(amountSold)}</OperationDetail>
                        {isOnOwnBlockchain && (
                            <OperationDetail label={'You spend'}>{renderAsSource(amountSpend)}</OperationDetail>
                        )}
                        <OperationDetail.Total label={'You receive'}>
                            {renderAsApprox(approxAmount)}
                        </OperationDetail.Total>
                    </OperationDetail.Layout>
                    <RsnButton.Primary type={'submit'}>Sell</RsnButton.Primary>
                </div>
            </form>

            <RsnDialog.Controller onClose={navigateBack}>
                {(() => {
                    switch (dialog) {
                        case 'fee-issue':
                            return <NotEnoughFundsDialog blockchain={accountFrom.blockchain} pathToBuy={pathToBuy} />;
                        case 'fee-info':
                            return <FeeHandlingDialog />;
                        case 'confirm-details':
                            return (
                                <ConfirmDetailsDialog
                                    accountFrom={accountFrom.name}
                                    accountTo={accountTo.name}
                                    fee={formatAsFee(currentFee)}
                                    amountSpend={formatAsSource(amountSpend)}
                                    amountSold={formatAsSource(amountSold)}
                                    feeCalculationType={form.values.fee}
                                    receive={formatAsApprox(approxAmount)}
                                    blockchain={accountFrom.blockchain.name}
                                    isOwnBlockchain={isOnOwnBlockchain}
                                    onConfirm={actions.confirmDetailsClick}
                                />
                            );
                    }
                })()}
            </RsnDialog.Controller>
        </>
    );
}

function renderAsyncOperationDetail(
    isFetching: boolean,
    formatter: (value: BigNumber.Value) => string,
    skeleton: {width: number; height: number}
) {
    function AsyncOperationDetail(value: BigNumber.Value) {
        if (isFetching) {
            return <RsnSkeleton {...skeleton} containerClassName='inline-block align-bottom' />;
        }
        return formatter(value);
    }
    return AsyncOperationDetail;
}

export function SellTxLoadingPage() {
    return (
        <RootLayout>
            <div className={'flex h-full flex-col'}>
                <div className={'flex flex-1 flex-col gap-6 p-4'}>
                    <OperationSelectButton.Skeleton />
                    <OperationSelectButton.Skeleton />
                </div>
                <div className={'flex flex-col gap-4 p-4'}>
                    <RsnAmountInput.Skeleton />
                    <OperationDetail.Layout>
                        <div className={'flex items-baseline justify-between'}>
                            <RsnSkeleton width={70} height={16} />
                            <RsnSkeleton width={80} height={16} />
                        </div>
                        <div className={'flex items-baseline justify-between'}>
                            <RsnSkeleton width={20} height={16} />
                            <RsnSkeleton width={70} height={16} />
                        </div>
                        <div className={'flex items-baseline justify-between'}>
                            <RsnSkeleton width={70} height={16} />
                            <RsnSkeleton width={100} height={24} />
                        </div>
                    </OperationDetail.Layout>
                    <RsnSkeleton height={48} borderRadius={16} />
                </div>
            </div>
        </RootLayout>
    );
}

type SellFormState = ReturnType<typeof SellFormState>;
const SellFormState = () => {
    const state = FormState({
        amount: RsnAmount.empty(),
        fee: define<FeeCalculationType>(FeeCalculationType.Included),
    });
    state.flags.fee.completed = true;
    return state;
};

type Values = SellFormState['values'];

export type SmsConfirmationFormState = ReturnType<typeof SmsConfirmationFormState>;
export const SmsConfirmationFormState = () =>
    FormState({
        codeFromSms: '',
    });

type DialogName = typeof DialogName.infer;
const DialogName = type("'fee-info' | 'confirm-details' | 'fee-issue'");

const SellDirection = type.never
    .or({
        view: "'form'",
        dialog: DialogName.optional(),
    })
    .or({
        view: "'two-fa-pick'",
        dialog: type.enumerated('method-picker', 'settings-prompt').optional(),
    })
    .or({
        view: "'two-fa-confirm'",
        provider: TwoFaProvider,
        delivery: SmsDeliveryStatus.optional(),
    })
    .or({
        view: "'finish'",
        qty: 'string',
        orderId: OrderId,
    });

function useFeeRequestData(values: Values, resources: SellTxResource) {
    const accountId = resources.accountFrom.id;
    const accountType = resources.accountFrom.type;
    const feeCalculationType = values.fee;
    const amount = RsnAmount.useMemo(values.amount);

    return useMemo<FeeRequestData>(
        () => ({
            accountId,
            accountType,
            feeCalculationType,
            amount: RsnAmount.toBigNumber(amount),
        }),
        [accountId, accountType, amount, feeCalculationType]
    );
}

type RequestCodeParams = {
    resending?: boolean;
    provider: TwoFaProvider;
};

const t = {
    notEnoughFunds: 'Not enough funds',
    minAmount: (min: string) => `Min: ${min}`,
};
